import './style.scss'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { PlayFill } from '@/assets/svg/play-fill'
import { PauseOutlined } from '@ant-design/icons'

interface IProps {
  onTextChange: (text: string) => void
  audioUrls: any
}

const HighlightTextarea = forwardRef((props: IProps, ref) => {
  const { onTextChange, audioUrls } = props
  const inputRef = useRef<HTMLDivElement>(null)
  const lastFocus = useRef<Range | null>(null)
  const audioRef = useRef<any>()
  const [playingId, setPlayingId] = useState<any>()

  useEffect(() => {
    inputRef.current?.addEventListener('paste', pasteText)

    return () => {
      inputRef.current?.removeEventListener('paste', pasteText)
    }
  }, [])

  useImperativeHandle(ref, () => ({
    initText: (text: string) => {
      initText(text)
    }
  }))

  const saveLastFocus = useCallback(() => {
    const selection = window.getSelection()
    if (selection?.rangeCount) {
      lastFocus.current = selection.getRangeAt(0)
    }
    onTextChange(inputRef.current?.innerHTML || '')
  }, [onTextChange])

  const restoreFocus = useCallback((range: Range) => {
    inputRef.current?.focus()
    const selection = window.getSelection()
    if (selection && range) {
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }, [])

  const handleInsertTag = useCallback(
    (text: string) => {
      const range = lastFocus.current
      if (!range || !inputRef.current?.isContentEditable) {
        return
      }

      restoreFocus(range)

      const tagNode = document.createElement('span')
      const breakTag = document.createElement('break')
      breakTag.className = 'highlight-tag'
      breakTag.setAttribute('text', text)
      tagNode.setAttribute('contentEditable', 'false')
      tagNode.appendChild(breakTag)

      const spaceNode = document.createTextNode(' ')

      range.deleteContents()
      range.insertNode(spaceNode)
      range.insertNode(tagNode)

      range.setStartAfter(spaceNode)
      range.collapse(true)

      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(range)
      }

      lastFocus.current = range
      onTextChange(inputRef.current?.innerHTML || '')
    },
    [onTextChange, restoreFocus]
  )

  const previewAudio = (key: string) => {
    if (playingId === key) {
      return closeAudio()
    }

    if (audioUrls[key]) {
      setPlayingId(key)
      const urls = audioUrls[key] || []
      const randomIndex = Math.floor(Math.random() * urls.length)
      const url = urls[randomIndex]
      const audio = new Audio()
      audio.src = url
      audio.play()

      audio.addEventListener('ended', () => {
        closeAudio()
      })

      audio.addEventListener('pause', function () {
        closeAudio()
      })
      audioRef.current = audio
    }
  }

  const closeAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause?.()
      audioRef.current.src = ''
    }
    setPlayingId(undefined)
  }

  const tags = [
    { text: '回答我', key: 'answer_me_urls' },
    { text: 'Tell me why', key: 'tell_me_why_urls' },
    { text: 'Look in my eyes', key: 'look_in_my_eyes_urls' },
    { text: '哎能能', key: 'yeah_i_can_urls' }
  ]

  const initText = (text: string) => {
    const replacedString = replaceText(text)
    if (inputRef.current) {
      inputRef.current.innerHTML = replacedString
      onTextChange(replacedString)
    }
  }

  const replaceText = (text: string) => {
    let replaceText = text?.replace(/\[(回答我|Tell me why|Look in my eyes|哎能能)\]/g, (match, p1) => {
      return `<span contenteditable="false"><break class="highlight-tag" text="${p1}"></break></span>`
    })
    if (replaceText && /\[(回答我|Tell me why|Look in my eyes|哎能能)\]$/.test(text)) {
      replaceText += ' ' // 添加空格
    }
    return replaceText
  }

  const pasteText = (e: ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData?.getData('text/plain')
    if (inputRef.current) {
      document.execCommand('insertText', false, text)
    }
  }

  return (
    <div className="highlight-textarea">
      <div
        ref={inputRef}
        className="input-area"
        contentEditable
        suppressContentEditableWarning
        placeholder='在这里输入你的问答文案，或点击上方"AI生成"...'
        onInput={saveLastFocus}
        onClick={saveLastFocus}
        onBlur={saveLastFocus}
      />

      <div className="tag-list">
        {tags.map((tag) => (
          <div key={tag.key} className="tag-item" onClick={() => handleInsertTag(tag.text)}>
            <div
              className="left"
              onClick={(e) => {
                e.stopPropagation()
                previewAudio(tag.key)
              }}
            >
              {playingId === tag.key ? <PauseOutlined /> : <PlayFill className="play" />}
            </div>
            <span className="gradient">{tag.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
})

HighlightTextarea.displayName = 'HighlightTextarea'

export default HighlightTextarea
